import React, { useState } from 'react';

import type { ForgotPasswordDTO } from '../../services/apiClient';
import { Link } from 'react-router-dom';

import AuthLayout from '../../components/layout/AuthLayout';
import { useUserApi } from '../../hooks/useUserApi';

const ForgotPassword: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null); // State for email error

  const { forgotPassword } = useUserApi();

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null); // Clear any existing error message
    setSuccessMessage(null); // Clear any existing success message
    setEmailError(null); // Clear email validation error

    // Validate email
    if (!email) {
      setEmailError('Email is required.');

      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address.');

      return;
    }

    const dto: ForgotPasswordDTO = { email };

    try {
      setIsSubmitting(true);
      const response = await forgotPassword(dto);

      if (response && response.success) {
        // Show success message
        setSuccessMessage('Reset link has been sent to your email address.');
        setEmail('');
        setEmailError('');
      } else {
        setErrorMessage('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrorMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div>
        <h2 className="font-['Archivo'] text-[28px] font-black text-text mb-1">Reset Password</h2>
        <p className="cg-label mb-7">// Enter your email to receive a reset link</p>

        {errorMessage && (
          <p className="text-[#f87171] font-['Space_Mono'] text-[10px] mb-4">{errorMessage}</p>
        )}
        {successMessage && (
          <p className="text-accent font-['Space_Mono'] text-[10px] mb-4">{successMessage}</p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="cg-label block mb-[7px]">Email Address</label>
            <input
              id="email"
              type="email"
              className="cg-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
            />
            {emailError && (
              <p className="text-[#f87171] font-['Space_Mono'] text-[10px] mt-1">{emailError}</p>
            )}
          </div>
          <button
            type="submit"
            className="cg-btn cg-btn-primary w-full h-11"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Link →'}
          </button>
        </form>

        <p className="mt-6 font-['Space_Mono'] text-[10px] text-center text-muted">
          Return to{' '}
          <Link
            to="/login"
            className="text-accent underline decoration-[rgba(146,222,113,0.3)]"
          >
            login
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
